import { FC } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  IconDashboard,
  IconShip,
  IconCalendarEvent,
  IconRoute,
  IconSettings,
  IconChevronRight,
} from '@tabler/icons-react';
import { cn } from '../../utils/cn';
import type { MyPartnerQuery } from '../../gql/graphql';
import {
  getPartnerStatus,
  getPartnerKycState,
} from '../../constants/partnerStatus';

type Partner = NonNullable<MyPartnerQuery['myPartner']>;

interface PartnerSidebarProps {
  partner: Partner | null | undefined;
  isAdmin?: boolean;
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  disabled?: boolean;
}

const PartnerSidebar: FC<PartnerSidebarProps> = ({
  partner,
  isAdmin = false,
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const navItems: NavItem[] = [
    {
      label: 'Dashboard',
      path: '/partner',
      icon: <IconDashboard size={20} />,
    },
    {
      label: 'Boats',
      path: '/partner/boats',
      icon: <IconShip size={20} />,
    },
    {
      label: 'Bookings',
      path: '/partner/bookings',
      icon: <IconCalendarEvent size={20} />,
    },
    {
      label: 'Trips',
      path: '/partner/trips',
      icon: <IconRoute size={20} />,
    },
    {
      label: 'Settings',
      path: '/partner/settings',
      icon: <IconSettings size={20} />,
      disabled: true, // Future feature
    },
  ];

  const handleNavClick = (path: string) => {
    navigate(path);
    onClose(); // Close mobile sidebar after navigation
  };

  const isActive = (path: string) => {
    if (path === '/partner') {
      return location.pathname === '/partner';
    }
    return location.pathname.startsWith(path);
  };

  const getStatusBadge = () => {
    if (!partner) return null;

    const status = getPartnerStatus(partner.status);

    return (
      <span
        className={cn(
          'inline-flex items-center rounded-full px-2 py-1 text-xs font-medium text-white',
          status.color,
        )}
      >
        {status.label}
      </span>
    );
  };

  const getKycBadge = () => {
    if (!partner) return null;

    const kyc = getPartnerKycState(partner.kycState);

    return (
      <span
        className={cn(
          'inline-flex items-center rounded-full px-2 py-1 text-xs font-medium text-white',
          kyc.color,
        )}
      >
        KYC: {kyc.label}
      </span>
    );
  };

  return (
    <>
      {/* Mobile sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 transform bg-white shadow-lg transition-transform duration-300 ease-in-out lg:hidden',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-full flex-col">
          {/* Mobile header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Partner Portal
            </h2>
            <button
              onClick={onClose}
              className="text-gray-600 hover:text-gray-900"
              aria-label="Close sidebar"
              type="button"
              tabIndex={0}
            >
              <IconChevronRight size={24} />
            </button>
          </div>

          {/* Partner info */}
          <div className="border-b border-gray-200 px-4 py-4">
            <div className="mb-2">
              <h3 className="text-sm font-medium text-gray-500">
                {isAdmin ? 'Admin Portal' : 'Partner'}
              </h3>
              <p className="mt-1 text-base font-semibold text-gray-900">
                {isAdmin
                  ? 'Platform Administrator'
                  : partner?.name || 'Loading...'}
              </p>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {isAdmin && (
                <span className="inline-flex items-center rounded-full bg-purple-600 px-2 py-1 text-xs font-medium text-white">
                  Admin
                </span>
              )}
              {getStatusBadge()}
              {getKycBadge()}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-2 py-4">
            <ul className="space-y-1">
              {navItems.map(item => {
                const active = isActive(item.path);
                return (
                  <li key={item.path}>
                    <button
                      onClick={() =>
                        !item.disabled && handleNavClick(item.path)
                      }
                      onKeyDown={e => {
                        if (
                          !item.disabled &&
                          (e.key === 'Enter' || e.key === ' ')
                        ) {
                          e.preventDefault();
                          handleNavClick(item.path);
                        }
                      }}
                      disabled={item.disabled}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                        active
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-100',
                        item.disabled && 'cursor-not-allowed opacity-50',
                      )}
                      aria-label={item.label}
                      aria-current={active ? 'page' : undefined}
                      type="button"
                      tabIndex={item.disabled ? -1 : 0}
                    >
                      <span
                        className={cn(
                          active ? 'text-blue-700' : 'text-gray-500',
                        )}
                      >
                        {item.icon}
                      </span>
                      <span className="flex-1 text-left">{item.label}</span>
                      {item.disabled && (
                        <span className="text-xs text-gray-400">Soon</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </aside>

      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-gray-200 bg-white shadow-sm lg:flex">
        {/* Partner info */}
        <div className="border-b border-gray-200 px-4 py-6">
          <div className="mb-2">
            <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500">
              {isAdmin ? 'Admin Portal' : 'Partner Portal'}
            </h3>
            <p className="mt-2 text-lg font-semibold text-gray-900">
              {isAdmin
                ? 'Platform Administrator'
                : partner?.name || 'Loading...'}
            </p>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {isAdmin && (
              <span className="inline-flex items-center rounded-full bg-purple-600 px-2 py-1 text-xs font-medium text-white">
                Admin
              </span>
            )}
            {getStatusBadge()}
            {getKycBadge()}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2 py-4">
          <ul className="space-y-1">
            {navItems.map(item => {
              const active = isActive(item.path);
              return (
                <li key={item.path}>
                  <button
                    onClick={() => !item.disabled && handleNavClick(item.path)}
                    disabled={item.disabled}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                      active
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-100',
                      item.disabled && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    <span
                      className={cn(active ? 'text-blue-700' : 'text-gray-500')}
                    >
                      {item.icon}
                    </span>
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.disabled && (
                      <span className="text-xs text-gray-400">Soon</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default PartnerSidebar;
